import {
  faArrowRightLong,
  faArrowTrendDown,
  faArrowTrendUp,
  type IconDefinition,
} from "@fortawesome/sharp-solid-svg-icons"
import type { WastewaterPCRActivityLevel } from "@phc/common/gen/threatAnalyticsGen"
import type React from "react"
import { WastewaterCategory } from "./wastewaterHeatmapConstants"

export const TARGET_GROUPS = {
  "Respiratory Diseases": [
    "SARS-CoV-2 N1",
    "RSV",
    "Influenza A",
    "Influenza B",
  ],
  "Gastrointestinal Diseases": [
    "Adenovirus 40/41",
    "Norovirus GI",
    "Norovirus GII",
  ],
  "Other Diseases": ["NVO", "Mpox Clade I", "Mpox Clade II", "Influenza H5"],
}

/** Stable key format used to correlate anomaly flags across endpoints. */
export const makeAnomalyKey = (targetName: string, samplingEndDate: string) =>
  `${targetName}__${samplingEndDate}`

export const getTrendIcon = (
  analysis?: WastewaterPCRActivityLevel
): IconDefinition => {
  if (analysis?.trend === "Upward") return faArrowTrendUp
  if (analysis?.trend === "Downward") return faArrowTrendDown
  return faArrowRightLong
}

export const hasActivity = (level: WastewaterCategory) => {
  return (
    level !== WastewaterCategory.NOT_DETECTED &&
    level !== WastewaterCategory.NOT_CALCULATED &&
    level !== WastewaterCategory.NO_DATA
  )
}

export const getLevel = (
  analysis?: WastewaterPCRActivityLevel
): WastewaterCategory => {
  if (!analysis?.wastewater_category) return WastewaterCategory.NO_DATA

  switch (analysis.wastewater_category) {
    case "HIGH":
      return WastewaterCategory.HIGH
    case "MEDIUM":
      return WastewaterCategory.MEDIUM
    case "LOW":
      return WastewaterCategory.LOW
    case "NOT DETECTED":
      return WastewaterCategory.NOT_DETECTED
    case "not calculated":
      return WastewaterCategory.NOT_CALCULATED
    default:
      return WastewaterCategory.NO_DATA
  }
}

const SUPERSCRIPT_DIGIT: Record<string, string> = {
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
}

/** e.g. 4 → "⁴", -2 → "⁻²" */
export const toSuperscriptExponent = (exponent: number): string => {
  const digits = String(Math.abs(exponent))
    .split("")
    .map(d => SUPERSCRIPT_DIGIT[d] ?? d)
    .join("")
  if (exponent < 0) return `⁻${digits}`
  if (exponent === 0) return "⁰"
  return digits
}

const decomposeScientific = (
  value: number
): { sigFig: string; exponent: number } | null => {
  if (value === 0 || Math.abs(value) < 1000) return null
  const expStr = value.toExponential(1)
  const [sigFig = "0", expPart = "0"] = expStr.split("e")
  return { sigFig, exponent: parseInt(expPart, 10) }
}

/** Plain-text scientific notation, e.g. 76000 → "7.6 × 10⁴". */
export const formatScientificString = (value: number): string => {
  if (value === 0) return "0"
  const parts = decomposeScientific(value)
  if (parts) {
    return `${parts.sigFig} × 10${toSuperscriptExponent(parts.exponent)}`
  }
  return value.toFixed(1)
}

/** Scientific notation for UI (Unicode × and superscript exponents). */
export const formatScientific = (num: number): React.ReactNode =>
  formatScientificString(num)

export const isRareDisease = (pathogen: string) => {
  return TARGET_GROUPS["Other Diseases"].includes(pathogen)
}
